import React from 'react';
import { motion } from 'framer-motion';
import { DollarSign, TrendingUp, TrendingDown, CreditCard, Receipt, PieChart } from 'lucide-react';
import FinancialOverviewComponent from '../components/dashboard/FinancialOverview';

const Financials = () => {
  const financialMetrics = [
    { label: 'Revenue (MTD)', value: '$2.4M', change: '+15.2%', trend: 'up', color: 'from-green-500 to-green-600' },
    { label: 'Outstanding AR', value: '$845K', change: '-8.3%', trend: 'down', color: 'from-blue-500 to-blue-600' },
    { label: 'Profit Margin', value: '22.5%', change: '+2.1%', trend: 'up', color: 'from-purple-500 to-purple-600' },
    { label: 'Cost per TEU', value: '$1,250', change: '-5.4%', trend: 'down', color: 'from-orange-500 to-orange-600' },
  ];

  const recentInvoices = [
    { id: 'INV-2025-089', customer: 'Global Trading Co.', amount: '$45,600', status: 'paid', date: '2025-10-28' },
    { id: 'INV-2025-090', customer: 'Pacific Imports', amount: '$32,450', status: 'pending', date: '2025-10-29' },
    { id: 'INV-2025-091', customer: 'Ocean Freight Ltd', amount: '$78,900', status: 'overdue', date: '2025-10-25' },
    { id: 'INV-2025-092', customer: 'Asia Connect', amount: '$56,300', status: 'paid', date: '2025-10-30' },
  ];

  const costBreakdown = [
    { category: 'Ocean Freight', amount: '$1.2M', percentage: 50 },
    { category: 'Inland Transportation', amount: '$480K', percentage: 20 },
    { category: 'Customs & Duties', amount: '$360K', percentage: 15 },
    { category: 'Documentation', amount: '$240K', percentage: 10 },
    { category: 'Other Expenses', amount: '$120K', percentage: 5 },
  ];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="bg-gradient-to-br from-green-600 via-emerald-600 to-teal-600 rounded-xl shadow-lg p-8 text-white">
        <div className="flex items-center space-x-3 mb-3">
          <DollarSign className="w-8 h-8" />
          <h1 className="text-3xl font-bold">Financial Overview</h1>
        </div>
        <p className="text-white/90 text-lg">
          Comprehensive financial analytics and revenue insights
        </p>
      </div>

      {/* Financial Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {financialMetrics.map((metric, index) => (
          <motion.div
            key={index}
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: index * 0.1 }}
            className={`bg-gradient-to-br ${metric.color} rounded-xl shadow-lg p-6 text-white`}
          >
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-medium opacity-80">{metric.label}</h3>
              {metric.trend === 'up' ? (
                <TrendingUp className="w-4 h-4" />
              ) : (
                <TrendingDown className="w-4 h-4" />
              )}
            </div>
            <p className="text-3xl font-bold mb-1">{metric.value}</p>
            <p className="text-sm opacity-80">{metric.change} vs last month</p>
          </motion.div>
        ))}
      </div>

      {/* Financial Overview Component */}
      <FinancialOverviewComponent />

      {/* Recent Invoices & Cost Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Invoices */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
        >
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center">
            <Receipt className="w-5 h-5 mr-2 text-[#0072FF]" />
            Recent Invoices
          </h3>
          <div className="space-y-3">
            {recentInvoices.map((invoice, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg"
              >
                <div className="flex items-center space-x-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    invoice.status === 'paid' ? 'bg-green-100 dark:bg-green-900/30' :
                    invoice.status === 'pending' ? 'bg-orange-100 dark:bg-orange-900/30' :
                    'bg-red-100 dark:bg-red-900/30'
                  }`}>
                    {invoice.status === 'paid' ? (
                      <CreditCard className="w-5 h-5 text-green-600" />
                    ) : invoice.status === 'pending' ? (
                      <TrendingUp className="w-5 h-5 text-orange-600" />
                    ) : (
                      <TrendingDown className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                  <div>
                    <div className="font-semibold text-slate-900 dark:text-white">{invoice.id}</div>
                    <div className="text-sm text-slate-600 dark:text-slate-400">{invoice.customer}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-slate-900 dark:text-white">{invoice.amount}</div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    invoice.status === 'paid' ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' :
                    invoice.status === 'pending' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400' :
                    'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400'
                  }`}>
                    {invoice.status.toUpperCase()}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Cost Breakdown */}
        <motion.div
          initial={{ y: 20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-white dark:bg-slate-900 rounded-xl shadow-sm p-6 border border-slate-200 dark:border-slate-800"
        >
          <h3 className="text-lg font-bold text-slate-900 dark:text-white mb-4 flex items-center">
            <PieChart className="w-5 h-5 mr-2 text-[#0072FF]" />
            Cost Breakdown (MTD)
          </h3>
          <div className="space-y-4">
            {costBreakdown.map((item, index) => (
              <div key={index}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-slate-900 dark:text-white">{item.category}</span>
                  <span className="text-sm font-bold text-slate-900 dark:text-white">{item.amount}</span>
                </div>
                <div className="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-2">
                  <div
                    className="h-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500"
                    style={{ width: `${item.percentage}%` }}
                  ></div>
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{item.percentage}% of total</div>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Financials;
